import { logger, setGlobals } from 'common-ui';
import { commonHeaderFooterAtgConfigAndEnvVars } from '../utils/commonHeaderFooterAtgConfigAndEnvVars';
import type { ThemeCreateFunction } from 'tsconfig/types';
import ReactDOM from 'react-dom/client';
import { CommonEmbeddedHeaderFooterApp } from '../CommonEmbeddedHeader/CommonEmbeddedHeaderFooterApp';
import type { EmotionJSX } from '@emotion/react/types/jsx-namespace';
import React from 'react';

interface BrandObject {
  currencyCode: string;
  currency?: string;
  country: string;
  brandId: string;
}

interface InitAppProps {
  brandObject: BrandObject;
  createTheme: ThemeCreateFunction;
  GlobalStyles?: () => EmotionJSX.Element;
}

/**
 * Initializes the common embedded header and footer application.
 *
 * This function sets up global configuration and environment variables,
 * creates a theme, and renders the `CommonEmbeddedHeaderFooterApp` React component
 * into the DOM element with the ID `newHeaderReactApp`.
 *
 * @param {InitAppProps} params - The initialization parameters.
 * @param {object} params.brandObject - Brand-specific configuration object.
 * @param {Function} params.createTheme - Function to create a theme instance.
 * @param {React.ComponentType} params.GlobalStyles - Global styles component.
 * @returns {() => void} A function that, when called, performs the initialization.
 *
 * @remarks
 * - Logs an error if the required window data island or root element is not found.
 * - Merges brand configuration with common header/footer configuration and environment variables.
 */
export const commonInitApp =
  ({ brandObject, createTheme, GlobalStyles }: InitAppProps): (() => void) =>
  () => {
    if (!window.newHeaderFooterInitData) {
      logger.error('window data island "newHeaderFooterInitData" not found');
    }
    setGlobals({
      ...commonHeaderFooterAtgConfigAndEnvVars(window.newHeaderFooterInitData, window.newHeaderFooterBuildVersion),
      //general
      ...brandObject,
    });

    const theme = createTheme();
    const rootElement = document.getElementById('newHeaderReactApp');
    if (!rootElement) {
      logger.error('Root element "newHeaderReactApp" not found');
      return;
    }
    const root = ReactDOM.createRoot(rootElement);
    root.render(
      <React.StrictMode>
        <CommonEmbeddedHeaderFooterApp theme={theme} GlobalStyles={GlobalStyles} />
      </React.StrictMode>,
    );
  };
